/**
 * WalkthroughCoachmark - single coachmark card (PROD-595).
 *
 * Renders one step of the demo walkthrough as a floating dialog
 * positioned above its anchor target (resolved via
 * `document.querySelector(step.anchorSelector)` +
 * `getBoundingClientRect()`). The coachmark itself is a portal-free
 * `role="dialog"` shell with the focus trap from
 * `src/components/FocusTrap.tsx` (PROD-571 reuse, do not reinvent).
 *
 * HC6 a11y:
 *   - `role="dialog" aria-modal="true"` on the shell.
 *   - `aria-labelledby` -> the canonical title node.
 *   - `aria-describedby` -> the anchor target id (fallback: the body).
 *   - First interactive element (Skip or Next) auto-focuses on mount.
 *   - Tab + Shift+Tab cycle within the card via the focus trap.
 *   - ESC dismisses (callers route through `useWalkthroughStore.dismiss`).
 *   - Enter advances (caller routes through `useWalkthroughStore.next`).
 *   - The background dim is `aria-hidden="true"` so screen readers stay
 *     on the coachmark, not the page chrome.
 *
 * Reduced motion:
 *   - Respects `prefers-reduced-motion: reduce` via a media-query check.
 *     Honored via class toggling - reduced motion = no transitions.
 *   - Animation is opt-in opacity only; we never use translate or scale.
 *
 * Anchor positioning:
 *   - `getBoundingClientRect()` on mount + on `resize` / `scroll` so the
 *     coachmark tracks its anchor through the live UI. If the selector
 *     resolves to no node, the coachmark falls back to viewport-center
 *     so the user is not stranded looking at an invisible card.
 *   - The anchor target itself is highlighted via a 2px ring and a
 *     subtle pulse (suppressed when reduced motion is set).
 *
 * HC5: zero em / en dashes anywhere in this file or rendered copy.
 */
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { useFocusTrap } from './FocusTrap';
import {
  WALKTHROUGH_FINISH_LABEL,
  WALKTHROUGH_NEXT_LABEL,
  WALKTHROUGH_PREV_LABEL,
  WALKTHROUGH_SKIP_LABEL,
  type WalkthroughStep,
} from '../data/walkthrough-script';

export interface WalkthroughCoachmarkProps {
  /** Step content (resolved + `{tenant}` already interpolated by caller). */
  step: WalkthroughStep;
  /** Resolved title + body for this step. */
  resolvedTitle: string;
  /** Resolved body, with any tokens already interpolated. */
  resolvedBody: string;
  /** 0-based step index (drives the dot row). */
  stepIndex: number;
  /** Total steps in the script (drives the dot row + Next vs Finish). */
  totalSteps: number;
  /** Advance handler. Caller routes through walkthrough.store.next. */
  onNext: () => void;
  /** Step-back handler. Caller routes through walkthrough.store.prev. */
  onPrev: () => void;
  /** Skip / dismiss handler. Caller routes through walkthrough.store.dismiss. */
  onSkip: () => void;
}

/** Read the prefers-reduced-motion media query in a SSR-safe way. */
function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (evt: MediaQueryListEvent) => setReduced(evt.matches);
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    // Older Safari fallback.
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, []);
  return reduced;
}

interface AnchorRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/**
 * Resolve the anchor element + its rect via the supplied CSS selector.
 * Recomputes on resize + scroll so the coachmark tracks the anchor as
 * the user manipulates the UI.
 */
function useAnchorRect(selector: string): AnchorRect | null {
  const [rect, setRect] = useState<AnchorRect | null>(null);

  useLayoutEffect(() => {
    if (typeof document === 'undefined') return;

    const recompute = () => {
      const el = document.querySelector(selector) as HTMLElement | null;
      if (!el) {
        setRect(null);
        return;
      }
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    recompute();

    window.addEventListener('resize', recompute);
    window.addEventListener('scroll', recompute, true);
    return () => {
      window.removeEventListener('resize', recompute);
      window.removeEventListener('scroll', recompute, true);
    };
  }, [selector]);

  return rect;
}

/**
 * Compute the coachmark card's pixel position from the anchor rect.
 * Strategy: place the card just below the anchor when there is room,
 * otherwise above. Horizontal centerline aligned to the anchor centerline,
 * clamped to the viewport with a 16px margin so the card never escapes
 * the visible area.
 */
function computeCardPosition(
  anchor: AnchorRect | null,
  cardWidth: number,
  cardHeight: number,
): { top: number; left: number } {
  // Default fallback: viewport-center.
  const vpW = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const vpH = typeof window !== 'undefined' ? window.innerHeight : 768;
  const margin = 16;

  if (!anchor) {
    return {
      top: Math.max(margin, (vpH - cardHeight) / 2),
      left: Math.max(margin, (vpW - cardWidth) / 2),
    };
  }

  const spaceBelow = vpH - (anchor.top + anchor.height);
  const placeBelow = spaceBelow > cardHeight + margin;
  const top = placeBelow
    ? anchor.top + anchor.height + margin
    : Math.max(margin, anchor.top - cardHeight - margin);

  const anchorCenter = anchor.left + anchor.width / 2;
  let left = anchorCenter - cardWidth / 2;
  if (left < margin) left = margin;
  if (left + cardWidth > vpW - margin) left = vpW - cardWidth - margin;

  return { top, left };
}

const CARD_WIDTH = 360;
const CARD_HEIGHT_ESTIMATE = 220;

export function WalkthroughCoachmark({
  step,
  resolvedTitle,
  resolvedBody,
  stepIndex,
  totalSteps,
  onNext,
  onPrev,
  onSkip,
}: WalkthroughCoachmarkProps) {
  const titleId = useId();
  const bodyId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const reducedMotion = usePrefersReducedMotion();
  const anchorRect = useAnchorRect(step.anchorSelector);
  const [cardSize, setCardSize] = useState({
    width: CARD_WIDTH,
    height: CARD_HEIGHT_ESTIMATE,
  });

  // Re-measure the card on mount + when the step changes so the
  // viewport-clamp uses the real height not the estimate.
  useLayoutEffect(() => {
    if (containerRef.current) {
      const r = containerRef.current.getBoundingClientRect();
      setCardSize({ width: r.width, height: r.height });
    }
  }, [step.id]);

  const position = computeCardPosition(anchorRect, cardSize.width, cardSize.height);

  // Focus trap. ESC dismisses (the trap calls onEscape, we route to skip).
  useFocusTrap(containerRef, {
    enabled: true,
    onEscape: onSkip,
  });

  // Enter advances. Hooked at the document level inside the trap so the
  // user can press Enter from any focused element inside the card.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        // Do NOT swallow Enter when focus is on a button - the button's
        // own click handler should fire. Only treat Enter as "advance"
        // when the focused element is not a button or link.
        const active = document.activeElement;
        const isButton =
          active instanceof HTMLElement &&
          (active.tagName === 'BUTTON' || active.tagName === 'A');
        if (isButton) return;
        event.preventDefault();
        onNext();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onNext]);

  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex === totalSteps - 1;
  const nextLabel = isLastStep ? WALKTHROUGH_FINISH_LABEL : WALKTHROUGH_NEXT_LABEL;
  const transitionClass = reducedMotion ? '' : 'transition-opacity duration-150';

  return (
    <>
      {/* Background dim layer. aria-hidden so screen readers stay on the
       * coachmark itself; clicking the dim does NOT dismiss (clinicians
       * are protected from accidental dismissal during the demo). */}
      <div
        aria-hidden="true"
        data-testid="walkthrough-backdrop"
        className={[
          'fixed inset-0 z-40 bg-text-primary/30 pointer-events-none',
          transitionClass,
        ]
          .filter(Boolean)
          .join(' ')}
      />

      {/* Anchor highlight ring - rendered as a separate fixed-positioned
       * element over the resolved anchor. Suppressed when no anchor was
       * resolved or when reduced motion is on. */}
      {anchorRect ? (
        <div
          aria-hidden="true"
          data-testid="walkthrough-anchor-highlight"
          style={{
            position: 'fixed',
            top: anchorRect.top - 4,
            left: anchorRect.left - 4,
            width: anchorRect.width + 8,
            height: anchorRect.height + 8,
            zIndex: 41,
            pointerEvents: 'none',
          }}
          className={[
            'rounded-signal-md ring-2 ring-signal-primary-500',
            reducedMotion ? '' : 'animate-pulse',
          ]
            .filter(Boolean)
            .join(' ')}
        />
      ) : null}

      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        data-testid="walkthrough-coachmark"
        data-step-id={step.id}
        data-step-index={stepIndex}
        data-total-steps={totalSteps}
        data-reduced-motion={reducedMotion ? 'true' : 'false'}
        tabIndex={-1}
        style={{
          position: 'fixed',
          top: position.top,
          left: position.left,
          width: CARD_WIDTH,
          zIndex: 42,
        }}
        className={[
          'flex flex-col gap-gutter-sm rounded-signal-lg bg-surface-base p-gutter-md shadow-signal-elevated ring-1 ring-surface-border',
          transitionClass,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <header className="flex items-start justify-between gap-gutter-sm">
          <div className="flex flex-col gap-1">
            <span
              data-testid="walkthrough-step-counter"
              className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary"
            >
              Step {stepIndex + 1} of {totalSteps}
            </span>
            <h2
              id={titleId}
              data-testid="walkthrough-coachmark-title"
              className="text-signal-lg font-semibold text-text-primary"
            >
              {resolvedTitle}
            </h2>
          </div>
          <button
            type="button"
            data-testid="walkthrough-skip-button"
            onClick={onSkip}
            aria-label={WALKTHROUGH_SKIP_LABEL}
            className="rounded-signal-md px-gutter-sm py-1 text-signal-xs font-semibold uppercase tracking-wide text-text-secondary hover:bg-surface-muted focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
          >
            {WALKTHROUGH_SKIP_LABEL}
          </button>
        </header>

        <p
          id={bodyId}
          data-testid="walkthrough-coachmark-body"
          className="text-signal-sm text-text-secondary"
        >
          {resolvedBody}
        </p>

        <div
          role="progressbar"
          aria-label="Walkthrough progress"
          aria-valuemin={1}
          aria-valuemax={totalSteps}
          aria-valuenow={stepIndex + 1}
          data-testid="walkthrough-progress-dots"
          className="flex items-center gap-1"
        >
          {Array.from({ length: totalSteps }, (_, i) => (
            <span
              key={i}
              role="presentation"
              aria-hidden="true"
              data-testid={`walkthrough-progress-dot-${i}`}
              data-active={i === stepIndex ? 'true' : 'false'}
              className={[
                'inline-block h-1.5 w-4 rounded-signal-pill',
                i === stepIndex
                  ? 'bg-signal-primary-600'
                  : 'bg-surface-border',
              ].join(' ')}
            />
          ))}
        </div>

        <footer className="flex items-center justify-between gap-gutter-sm pt-gutter-sm">
          <button
            type="button"
            data-testid="walkthrough-prev-button"
            onClick={onPrev}
            disabled={isFirstStep}
            aria-label={WALKTHROUGH_PREV_LABEL}
            className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-md py-1 text-signal-sm font-semibold text-text-primary hover:bg-surface-muted focus:outline-none focus:ring-2 focus:ring-signal-primary-500 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {WALKTHROUGH_PREV_LABEL}
          </button>
          <button
            type="button"
            data-testid="walkthrough-next-button"
            onClick={onNext}
            aria-label={nextLabel}
            className="rounded-signal-md bg-signal-primary-600 px-gutter-md py-1 text-signal-sm font-semibold text-text-inverse hover:bg-signal-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-signal-primary-500"
          >
            {nextLabel}
          </button>
        </footer>
      </div>
    </>
  );
}
